import { Controller } from "@hotwired/stimulus"

const MAX_SECONDS = 30

export default class extends Controller {
  static targets = ["button", "status", "lastRun", "metaStatus"]
  static values = { url: String }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  disconnect() {
    this._stopTimer()
  }

  async run(event) {
    event.preventDefault()
    if (this.running) return
    this.running = true
    this.secondsElapsed = 0

    this._showRunning()
    this._startTimer()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      })

      const data = await response.json()
      this._stopTimer()
      this._showComplete(data)
    } catch (error) {
      console.error("Alert run failed:", error)
      this._stopTimer()
      this._showComplete({ summary: "Network error — could not reach the server. Please try again.", triggered: false, error: true, match_count: 0 })
    }
  }

  _startTimer() {
    this._updateCountdown()
    this.timerInterval = setInterval(() => {
      this.secondsElapsed++
      this._updateCountdown()
    }, 1000)
  }

  _stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  _updateCountdown() {
    const remaining = Math.max(0, MAX_SECONDS - this.secondsElapsed)
    const timerEl = this.hasStatusTarget && this.statusTarget.querySelector("[data-timer]")
    if (timerEl) {
      timerEl.textContent = `~${remaining}s remaining`
    }
  }

  _showRunning() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add("alert-run-btn-disabled")
      this.buttonTarget.setAttribute("aria-disabled", "true")
    }

    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <span class="alert-run-status alert-run-status-running">
          <svg class="alert-run-gear spinning" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z"></path></svg>
          <span class="alert-run-text">Alert Is Running</span>
          <span class="alert-run-timer" data-timer>~${MAX_SECONDS}s remaining</span>
        </span>
        <span class="alert-run-nav-hint">You can safely navigate away &mdash; the alert will keep running.</span>`
      this.statusTarget.style.display = ""
    }

    if (this.hasMetaStatusTarget) {
      this._updateMetaStatus({ last_run_status: "evaluating" })
    }
  }

  _showComplete(data) {
    this.running = false

    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove("alert-run-btn-disabled")
      this.buttonTarget.removeAttribute("aria-disabled")
    }

    if (this.hasStatusTarget) {
      const hasMatches = data.match_count > 0
      const checkIcon = `<svg class="alert-run-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9 12l2 2 4-4"></path></svg>`
      const clearIcon = `<svg class="alert-run-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="8" y1="12" x2="16" y2="12"></line></svg>`
      const warnIcon = `<svg class="alert-run-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`
      const errorIcon = `<svg class="alert-run-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`
      const bgIcon = `<svg class="alert-run-gear spinning" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z"></path></svg>`
      const chevronIcon = `<svg class="alert-expand-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`

      // Three distinct states: error, timed_out (background), or complete
      if (data.error) {
        let html = `
          <span class="alert-run-status alert-run-status-error">
            ${errorIcon}
            <span class="alert-run-text">Alert Evaluation Failed</span>
          </span>
          <span class="alert-run-summary">${this._escapeHtml(data.summary)}</span>`
        this.statusTarget.innerHTML = html
        this.statusTarget.style.display = ""
        return
      }

      if (data.timed_out) {
        let html = `
          <span class="alert-run-status alert-run-status-running">
            ${bgIcon}
            <span class="alert-run-text">Processing in Background</span>
          </span>
          <span class="alert-run-summary">${this._escapeHtml(data.summary)}</span>`
        this.statusTarget.innerHTML = html
        this.statusTarget.style.display = ""
        return
      }

      // Check if all geographies were skipped due to insufficient data
      const hasDataIssue = data.data_weeks !== undefined && data.data_weeks < 2

      let statusIcon, statusClass
      if (hasDataIssue) {
        statusIcon = warnIcon
        statusClass = "alert-run-status-warning"
      } else if (hasMatches) {
        statusIcon = checkIcon
        statusClass = "alert-run-status-matched"
      } else {
        statusIcon = clearIcon
        statusClass = "alert-run-status-complete"
      }

      // Build the status line
      let html = `
        <span class="alert-run-status ${statusClass}">
          ${statusIcon}
          <span class="alert-run-text">Alert Run Complete</span>
        </span>`

      // Render results in the same collapsible <details> format as server-rendered results
      if (data.conditions && data.conditions.length > 0) {
        const now = new Date()
        const dateStr = now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
        const timestamp = `${dateStr} at ${timeStr}`

        html += `<div class="alert-dates-section" style="border-top: none; padding-left: 0; padding-right: 0;">
          <div class="alert-dates-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            Alert Results
          </div>
          <div class="alert-dates-list">
            <details class="alert-date-entry alert-date-unread" open>
              <summary class="alert-date-header">
                ${chevronIcon}
                <span class="alert-date-text">${this._escapeHtml(timestamp)}</span>
                <span class="alert-date-badge">New</span>
                <span class="alert-date-summary">${this._escapeHtml(data.summary)}</span>
              </summary>
              <div class="alert-date-body">`

        for (const cond of data.conditions) {
          const diag = cond.diagnostics || {}

          if (cond.triggered && cond.geographies && cond.geographies.length > 0) {
            html += this._buildResultsTable(cond)
          } else {
            const reason = this._buildDiagReason(diag)
            if (reason) {
              html += `<div class="alert-run-cond alert-run-cond-clear">
                <div class="alert-run-cond-header">
                  <span class="alert-run-cond-status">No matches</span>
                  <span class="alert-run-cond-desc">${this._escapeHtml(cond.description)}</span>
                </div>
                <div class="alert-run-cond-reason">${reason}</div>
              </div>`
            }
          }
        }

        html += `</div></details></div></div>`
      }

      this.statusTarget.innerHTML = html
      this.statusTarget.style.display = ""
    }

    if (this.hasLastRunTarget && data.last_evaluated_at) {
      this.lastRunTarget.textContent = `Last run ${data.last_evaluated_at}`
      this.lastRunTarget.style.display = ""
    }

    if (this.hasMetaStatusTarget) {
      this._updateMetaStatus(data)
    }
  }

  _updateMetaStatus(data) {
    const clearIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="8" y1="12" x2="16" y2="12"></line></svg>`
    const failIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`
    const spinIcon = `<svg class="spinning" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z"></path></svg>`

    const status = data.last_run_status
    if (status === "no_matches") {
      this.metaStatusTarget.innerHTML = `<span class="alert-meta-status alert-meta-status-clear">${clearIcon} No matches found</span>`
    } else if (status === "failed" || data.error) {
      this.metaStatusTarget.innerHTML = `<span class="alert-meta-status alert-meta-status-failed">${failIcon} Evaluation failed</span>`
    } else if (status === "evaluating" || data.timed_out) {
      this.metaStatusTarget.innerHTML = `<span class="alert-meta-status alert-meta-status-evaluating">${spinIcon} Processing in background&hellip;</span>`
    } else {
      // "matched" — clear the pill since the results panel already shows the outcome
      this.metaStatusTarget.innerHTML = ""
    }
  }

  _buildResultsTable(cond) {
    const geos = cond.geographies || []
    const total = cond.total_geo_count || geos.length

    let html = `<div class="alert-results-table-wrap" data-controller="alert-results">`

    // Header with condition description and selection actions
    html += `<div class="alert-results-header">
      <span class="alert-results-cond-desc">${this._escapeHtml(cond.description)}</span>
      <div class="alert-results-actions">
        <span class="alert-results-count">${total} ${total === 1 ? 'market' : 'markets'} matched</span>
        <span class="mf-selection-count" data-alert-results-target="count"></span>
        <button type="button" class="btn btn-secondary btn-xs" data-alert-results-target="clearBtn" data-action="click->alert-results#clearAll" style="display:none">Clear</button>
        <button type="button" class="btn btn-primary btn-xs" data-alert-results-target="viewBtn" data-action="click->alert-results#openDashboard" disabled>View in Dashboard</button>
      </div>
    </div>`

    // Table
    html += `<div class="table-wrapper alert-results-scroll"><table class="data-table alert-results-table">
      <thead><tr>
        <th class="ar-check"></th>
        <th class="ar-rank">#</th>
        <th class="ar-market">Market</th>
        <th class="ar-change">Change</th>
      </tr></thead><tbody>`

    for (let i = 0; i < geos.length; i++) {
      const geo = geos[i]
      html += `<tr>
        <td class="ar-check"><input type="checkbox" value="${geo.geography_id || ''}" class="mf-checkbox" data-alert-results-target="checkbox" data-action="change->alert-results#toggle"${geo.geography_id ? '' : ' disabled'}></td>
        <td class="ar-rank">${i + 1}</td>
        <td class="ar-market">${this._escapeHtml(geo.name)}</td>
        <td class="ar-change">${geo.change ? this._escapeHtml(geo.change) : '—'}</td>
      </tr>`
    }

    html += `</tbody></table></div>`
    html += `</div>`
    return html
  }

  _buildDiagReason(diag) {
    if (!diag || !diag.checked) return ""
    const parts = []
    if (diag.checked > 0) {
      parts.push(`Checked ${diag.checked} market${diag.checked === 1 ? '' : 's'}`)
    }
    if (diag.insufficient_weeks > 0 && diag.insufficient_weeks === diag.checked) {
      parts.push(`all had only 1 week of data (need 2+ weeks to detect changes)`)
    } else if (diag.insufficient_weeks > 0) {
      parts.push(`${diag.insufficient_weeks} had insufficient data`)
    }
    if (diag.no_data > 0) {
      parts.push(`${diag.no_data} had no data in the lookback window`)
    }
    if (diag.below_threshold > 0) {
      parts.push(`${diag.below_threshold} changed but didn't exceed threshold`)
    }
    return parts.length > 0 ? parts.join(' — ') : ""
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML
  }
}
